<template>
  <div class="seat-map-navigation">
    <div
      class="d-none d-lg-block d-xl-block seat-map-navigation flex-xl-column flex-lg-column flex-md-row flex-sm-row flex-xs-row justify-content-between justify-content-xl-start justify-content-lg-start gap-10px"
      role="tablist"
    >
      <seat-navigation-accordion-block
        v-for="segment in segments"
        :key="segment.segmentId"
        :segment="segment"
        :everyoneSeated="isEveryoneSeated(segment.segmentId)"
        :isOpen="segment.segmentId === currentSegmentId"
        :onOpen="
          () => {
            if (segment.segmentId !== currentSegmentId) {
              setCurrentSegmentId(segment.segmentId);
            }
          }
        "
      >
        <template v-slot:flight>
          <div class="segment-info thin d-flex px-2">
            <img
              class="carrier-logo"
              :alt="segment.carrierCode"
              :src="
                'https://static.vadobay.com/airlines-logos/200x100/' + segment.carrierCode + '.png'
              "
            />
            <span class="font-size-14px">{{ aircraftCode }}</span>
            <span v-if="carriers[segment.carrierCode]" class="font-size-14px"
              >({{ carriers[segment.carrierCode] }})</span
            >
            <span v-else class="font-size-14px">({{ segment.carrierCode }})</span>
          </div>
        </template>
        <template v-slot:deck v-if="availableDecks.length > 1">
          <selector
            :disabled="disabled"
            label="Selezione deck"
            :selected="currentDeckIndex"
            :options="availableDecks"
            :onChange="(v) => setCurrentDeckIndex(v)"
            showProgress
          >
            <template v-slot:inside>
              <div class="segment-info thin d-flex">
                <span>{{ currentDeckIndex }}</span>
              </div>
            </template>
          </selector>
        </template>
        <template v-slot:passengers>
          <div class="selected-seats d-flex w-100">
            <div class="m-0 p-0 w-100 d-flex justify-content-between align-items-center">
              <div class="font-size-14px bold">Selezione del passeggero</div>
              <b-button
                v-if="arePassengersSeatedOnSegment(segment.segmentId)"
                class="m-0 p-0 border-0 small"
                variant="ghost"
                role="button"
                @click="
                  () => {
                    resetSegmentSelections(segment.segmentId);
                    if (passengerOptions.length) [setCurrentPassengerId(passengerOptions[0].value)];
                  }
                "
              >
                <span class="small">Cancella</span>
              </b-button>
            </div>
            <passenger-selection-row
              v-for="passenger in sittingPassengers"
              :key="passenger.id"
              :passenger="passenger"
              :seat="getPassengerSeat(segment.segmentId, passenger.id)"
              :selected="currentPassengerId === passenger.id"
              :onClick="() => setCurrentPassengerId(passenger.id)"
              emptyNotSelected
            />
          </div>
        </template>
      </seat-navigation-accordion-block>
    </div>

    <div
      class="d-flex d-lg-none d-xl-none seat-map-navigation flex-xl-column flex-lg-column flex-md-row flex-sm-row flex-xs-row justify-content-between justify-content-xl-start justify-content-lg-start gap-10px"
    >
      <div :class="availableDecks.length > 1 ? 'flex-basis-mobile-30' : 'flex-basis-mobile-50'">
        <selector
          label="Selezione volo"
          :disabled="disabled"
          :selected="currentSegmentId"
          :options="segmentsOptions"
          :onChange="(v) => setCurrentSegmentId(v)"
          showProgress
        >
          <template v-slot:inside>
            <span class="segment-info thin d-flex">
              <span>{{ aircraftCode }}</span>
              <span v-if="carriers[currentCarrierIata]">({{ carriers[currentCarrierIata] }})</span>
              <span v-else>({{ currentCarrierIata }})</span>
            </span>
          </template>
        </selector>
      </div>
      <div v-if="availableDecks.length > 1" class="flex-basis-mobile-30">
        <selector
          :disabled="disabled"
          label="Selezione deck"
          :selected="currentDeckIndex"
          :options="availableDecks"
          :onChange="(v) => setCurrentDeckIndex(v)"
          showProgress
        >
          <template v-slot:inside>
            <div class="segment-info thin d-flex">
              <span>{{ currentDeckIndex }}</span>
            </div>
          </template>
        </selector>
      </div>
      <div
        :class="
          availableDecks.length > 1 ? 'flex-basis-mobile-30' : 'flex-basis-mobile-50 flex-grow'
        "
      >
        <selector
          label="Selezione passeggero"
          :disabled="disabled"
          :selected="currentPassengerId"
          :options="passengerOptions"
          :onChange="(v) => setCurrentPassengerId(v)"
          showProgress
        >
          <template v-slot:inside>
            <div class="segment-info thin d-flex">
              <span v-if="currentPassengerSeat">{{ currentPassengerSeat }}</span>
              <span v-else>Selezione</span>
              <span v-if="isCurrentPassengerHoldingAnInfant" class="bold">con bebè</span>
            </div>
          </template>
        </selector>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPassengerName, formatSegmentLabel } from '@/utilities';
import Selector from '@/components/blocks/selector.vue';
import PassengerSelectionRow from '@/components/blocks/seat-map/passenger-selection-row.vue';
import SeatNavigationAccordionBlock from './seat-navigation-accordion-block.vue';

export default {
  name: 'seat-map-navigation',
  components: {
    selector: Selector,
    'passenger-selection-row': PassengerSelectionRow,
    'seat-navigation-accordion-block': SeatNavigationAccordionBlock,
  },
  props: {
    disabled: Boolean,

    segments: Array,
    currentSegmentIndex: Number,
    currentSegmentId: String,
    setCurrentSegmentId: Function,

    availableDecks: Array,
    currentDeckIndex: String,
    setCurrentDeckIndex: Function,

    sittingPassengers: Array,
    currentPassengerId: String,
    setCurrentPassengerId: Function,

    parsedSelections: Object,
    resetSegmentSelections: Function,
  },
  data() {
    return {
      passengerOptions: this.sittingPassengers.map((p) => ({
        value: p.id,
        label: this.formatPassengerName(p),
      })),
      segmentsOptions: this.segments.map((s) => ({
        value: s.segmentId,
        label: this.formatSegmentLabel(s),
        disabled: [].includes(s.segmentId),
      })),
    };
  },
  computed: {
    carriers() {
      return this.$store.state.liveflight.results.dictionaries.carriers;
    },
    currentCarrierIata() {
      if (this.segments.length && this.segments[this.currentSegmentIndex])
        return this.segments[this.currentSegmentIndex].carrierCode;
      return '';
    },
    aircraftCode() {
      if (this.segments.length && this.segments[this.currentSegmentIndex])
        return this.segments[this.currentSegmentIndex].aircraft.code;
      return '';
    },
    isCurrentPassengerHoldingAnInfant() {
      const index = this.sittingPassengers.findIndex((p) => p.id === this.currentPassengerId);
      if (index < 0) {
        return false;
      }
      return this.sittingPassengers[index].holding;
    },
    currentPassengerSeat() {
      if (!this.parsedSelections) {
        return '';
      }
      if (!this.parsedSelections[this.currentSegmentId]) {
        return '';
      }

      const seat = this.parsedSelections[this.currentSegmentId][this.currentPassengerId] || {};
      return seat.number || '';
    },
    seatedPassengersForCurrentSegment() {
      const segmentSelections = this.parsedSelections[this.currentSegmentId] || {};
      return this.sittingPassengers.filter((p) => segmentSelections[p.id]);
    },
  },
  methods: {
    formatPassengerName,
    formatSegmentLabel,
    arePassengersSeatedOnSegment(segmentId) {
      return Object.keys(this.parsedSelections[segmentId] || {}).length > 0;
    },
    getPassengerSeat(segmentId, passengerId) {
      const segmentSelections = this.parsedSelections[segmentId] || {};
      return segmentSelections[passengerId];
    },
    isEveryoneSeated(segmentId) {
      return (
        Object.keys(this.parsedSelections[segmentId] || {}).length === this.sittingPassengers.length
      );
    },
  },
};
</script>

<style lang="scss">
.seat-map-navigation {
  flex: 1;
  gap: 16px;

  .selected-seats {
    padding: 8px;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
